// @ts-nocheck

export type LocationFilter = {
  state?: string
  city?: string
}

export const normalizeLocation = (
  location: string | LocationFilter | undefined | null
): LocationFilter | undefined => {
  if (!location) {
    return undefined
  }

  if (typeof location === 'string') {
    return location ? { city: location } : undefined
  }

  if (!location.state && !location.city) {
    return undefined
  }

  return {
    ...(location.state && { state: location.state }),
    ...(location.city && { city: location.city })
  }
}

export const hasLocationFilter = (
  location: string | LocationFilter | undefined | null
): boolean => {
  const normalized = normalizeLocation(location)
  return Boolean(normalized?.state || normalized?.city)
}

export const getLocationDisplayLabel = (
  location: string | LocationFilter | undefined | null,
  stateOptions?: { value: string; label: string }[]
): string | undefined => {
  const normalized = normalizeLocation(location)
  if (!normalized) {
    return undefined
  }

  if (normalized.city) {
    return normalized.city
  }

  if (normalized.state) {
    const stateLabel = stateOptions?.find(
      (option) => option.value === normalized.state
    )?.label
    return stateLabel ?? normalized.state
  }

  return undefined
}

type EventAddress = {
  city?: string
  state?: string
}

/** Lower number = higher priority when sorting event lists. */
export const getLocationSortPriority = (
  eventAddress: EventAddress | undefined | null,
  location?: LocationFilter | null
): number => {
  const preferred = normalizeLocation(location)
  if (!preferred) {
    return 1
  }

  const eventCity = eventAddress?.city?.toLowerCase()
  const eventState = eventAddress?.state?.toLowerCase()
  const preferredCity = preferred.city?.toLowerCase()
  const preferredState = preferred.state?.toLowerCase()

  if (preferredCity && eventCity === preferredCity) {
    return 0
  }

  if (preferredState && eventState === preferredState) {
    return preferredCity ? 1 : 0
  }

  return preferredCity ? 2 : 1
}

/** Prefer events in the selected state/city without excluding others. */
export const sortEventsByLocationPreference = <
  T extends { address?: EventAddress | null }
>(
  events: T[],
  location?: string | LocationFilter | null
): T[] => {
  const preferred = normalizeLocation(location)
  if (!hasLocationFilter(preferred)) {
    return events
  }

  return [...events].sort(
    (a, b) =>
      getLocationSortPriority(a.address, preferred) -
      getLocationSortPriority(b.address, preferred)
  )
}
